import Client from '../Client';
import * as _ from 'es-toolkit';
import i18n from '../../locales/i18n'
import { parseApiResponseJson, resolveApiErrorMessage } from './getApiErrorMessage';
import store from '../../redux';
import { isEditionNavigationReadonlyPast } from '../editionNavUi';

class RestClient extends Client {
    constructor(config) {
        super();

        this.LIST_PATH   = '/list';
        /** Nap POST /:resource/search — same filters as GET list, JSON body (no URL length limit). */
        this.SEARCH_POST_PATH = '/search';
        this.GET_PATH    = '/get/id';
        this.UPDATE_PATH = '/update/id';
        this.CREATE_PATH = '/create';
        this.DELETE_PATH = '/delete/id';

        this.entryPoint = config.entryPoint;
        this.resource   = config.resource;
        this.resources  = config.resources;
        this.model      = config.model;
        this.id_field   = config.id_field || 'id';
        this.sortBy     = config.sortBy;
    }

    parseResponseJson(response) {
        return parseApiResponseJson(response, this.get_last_request_id?.(), {
            ...(this.get_last_request_meta?.() || {}),
            status: response?.status,
        });
    }

    /**
     * Build a user-facing Error, reporting unexpected API failures to Yoda.
     *
     * @param {object} [json]
     * @param {string} [fallback]
     * @param {object} [extraContext]
     * @returns {Promise<Error>}
     */
    async buildApiError(json, fallback, extraContext = {}) {
        const message = await resolveApiErrorMessage(
            json,
            fallback,
            this.get_last_request_id?.(),
            {
                ...(this.get_last_request_meta?.() || {}),
                ...extraContext,
            },
        );
        return new Error(message);
    }

    assertNotReadonlyEdition() {
        if (isEditionNavigationReadonlyPast(store.getState())) {
            throw new Error(i18n.t('editions.readonly_error'));
        }
    }

    list(params) {
        // remove empty filter values
        params = _.pickBy(params, _.identity);

        let field_list = params.fields;
        if (field_list === undefined || field_list === null) {
            field_list = typeof this.model.getListingFields === 'function'
                ? this.model.getListingFields()
                : [];
        }
        if (!Array.isArray(field_list))
            field_list = [];

        const fields = field_list
            .map((f) => String(f).trim())
            .filter((f) => f.length > 0 && /^[\w.]+$/.test(f))
            .join(',');

        const query = { ...params };
        if (fields)
            query.fields = fields;
        else
            delete query.fields;

        return this.GET(`/${this.entryPoint}${this.LIST_PATH}`, query)
            .then(async (response) => {
                const json = await this.parseResponseJson(response);
                const status_ctx = { status: response.status };
                if (!response.ok)
                    throw await this.buildApiError(json, undefined, status_ctx);

                if (json[this.resources]) {
                    json[this.resources] = json[this.resources].map(r => new this.model(r));

                    if (this.sortBy)
                        json[this.resources] = _.sortBy(json[this.resources], this.sortBy);

                    return json;
                }
                throw await this.buildApiError(json, undefined, status_ctx);
            });
    }

    /**
     * Same semantics as list() but uses Nap POST /:resource/search with a JSON body.
     * Use for heavy filters (e.g. long having_booked_screening_ids) that exceed HTTP request-line limits.
     */
    searchPost(params) {
        params = _.pickBy(params, _.identity);

        let field_list = params.fields;
        if (field_list === undefined || field_list === null) {
            field_list = typeof this.model.getListingFields === 'function'
                ? this.model.getListingFields()
                : [];
        }
        if (!Array.isArray(field_list))
            field_list = [];

        const fields = field_list
            .map((f) => String(f).trim())
            .filter((f) => f.length > 0 && /^[\w.]+$/.test(f))
            .join(',');

        const body = { ...params };
        if (fields)
            body.fields = fields;
        else
            delete body.fields;

        return this.POST(`/${this.entryPoint}${this.SEARCH_POST_PATH}`, body, null)
            .then(async (response) => {
                const json = await this.parseResponseJson(response);
                const status_ctx = { status: response.status };
                if (!response.ok)
                    throw await this.buildApiError(json, undefined, status_ctx);

                if (json[this.resources]) {
                    json[this.resources] = json[this.resources].map(r => new this.model(r));

                    if (this.sortBy)
                        json[this.resources] = _.sortBy(json[this.resources], this.sortBy);

                    return json;
                }
                throw await this.buildApiError(json, undefined, status_ctx);
            });
    }

    get(id, fields, params) {
        let url = `/${this.entryPoint}${this.GET_PATH}/${id}`;
        if (fields?.length > 0)
            url += `?fields=${fields.join(',')}`;

        // remove params empty values
        params = _.pickBy((params || {}), _.identity);

        return this.GET(url, params)
            .then(async (response) => {
                const json = await this.parseResponseJson(response);
                const status_ctx = { status: response.status };
                if (!response.ok)
                    throw await this.buildApiError(json, undefined, status_ctx);

                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }
                throw await this.buildApiError(json, undefined, status_ctx);
            });
    }

    create(payload, params) {
        this.assertNotReadonlyEdition();
        // remove empty filter values
        params = _.pickBy(params || {}, _.identity);

        payload = new this.model(payload);

        if ('prepareForUpdate' in payload)
            payload = payload.prepareForUpdate();

        return this.POST(`/${this.entryPoint}${this.CREATE_PATH}`, {
            lang: i18n.language,
            [this.resource]: payload
        }, params).then(async response => {
            const json = await this.parseResponseJson(response);
            if (!response.ok) {
                if (json.invalid) {
                    const messages = [];
                    for (let [key, error] of Object.entries(json.invalid)) {
                        key = key.replaceAll(/\.\d+/gm, '.*');
                        messages.push(i18n.t(`${this.resources}.invalid.${key}.${error}`));
                    }
                    throw new Error(_.uniq(messages).join(', '));
                }

                throw await this.buildApiError(json);
            }

            // Kore message send (and similar RPC-style creates) returns a
            // boolean success sentinel instead of a persisted resource.
            if (json[this.resource] === true)
                return json;

            if (json[this.resource]) {
                json[this.resource] = new this.model(json[this.resource]);
                if (this.id_field && !json[this.resource][this.id_field]) {
                    throw await this.buildApiError(json);
                }
                return json;
            }

            if (json.invalid) {
                const messages = [];
                for (let [key, error] of Object.entries(json.invalid)) {
                    key = key.replaceAll(/\.\d+/gm, '.*');
                    messages.push(i18n.t(`${this.resources}.invalid.${key}.${error}`));
                }
                throw new Error(_.uniq(messages).join(', '));
            }

            throw await this.buildApiError(json);
        });
    }

    upsert_by_maccsbox_id(payload, params) {
        this.assertNotReadonlyEdition();
        params = _.pickBy(params || {}, _.identity);

        const data = payload instanceof this.model ? { ...payload } : { ...payload };

        return this.POST(`/${this.entryPoint}/upsert_by_maccsbox_id`, {
            lang: i18n.language,
            [this.resource]: data,
        }, params)
            .then(response => this.parseResponseJson(response))
            .then(async (json) => {
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }

                if (json.invalid) {
                    const messages = [];
                    for (let [key, error] of Object.entries(json.invalid)) {
                        key = key.replaceAll(/\.\d+/gm, '.*');
                        messages.push(i18n.t(`${this.resources}.invalid.${key}.${error}`));
                    }
                    throw new Error(_.uniq(messages).join(', '));
                }

                throw await this.buildApiError(json);
            });
    }

    /**
     * Lookup a single resource by maccsbox_id (distributor-specific Kore endpoint).
     *
     * @param {string} maccsbox_id
     * @param {string[]} [fields]
     * @returns {Promise<Object|null>} `{ [resource]: Model }` or null when not found
     */
    get_by_maccsbox_id(maccsbox_id, fields) {
        const needle = String(maccsbox_id || '').trim();
        if (!needle)
            return Promise.resolve(null);

        const params = { maccsbox_id: needle };
        if (fields?.length)
            params.fields = fields.join(',');

        return this.GET(`/${this.entryPoint}/by_maccsbox_id`, params)
            .then(response => {
                if (response.status === 404)
                    return null;
                return this.parseResponseJson(response).then(async (json) => {
                    if (json[this.resource]) {
                        return {
                            [this.resource]: new this.model(json[this.resource]),
                        };
                    }
                    throw await this.buildApiError(json);
                });
            });
    }

    update(payload, options = {}) {
        if (!options.skip_readonly_edition_guard) {
            this.assertNotReadonlyEdition();
        }
        const submitted = payload;
        payload = new this.model(payload);

        if ('prepareForUpdate' in payload)
            payload = payload.prepareForUpdate();

        // Model constructors materialize defaults (empty refs, …). Subclasses
        // drop fields the caller did not send so a partial PUT cannot wipe them.
        if (typeof this.finalizeUpdatePayload === 'function')
            payload = this.finalizeUpdatePayload(submitted, payload);

        return this.PUT(
                `/${this.entryPoint}${this.UPDATE_PATH}/${payload[this.id_field]}`,
                { lang: i18n.language, [this.resource]: payload }
            )
            .then(async (response) => {
                const status_ctx = { status: response.status };
                const text = await response.text();
                if (!text || !text.trim()) {
                    // Never treat an empty error/404 body as success (silent no-op reload).
                    if (!response.ok) {
                        throw await this.buildApiError({}, undefined, status_ctx);
                    }
                    return { [this.resource]: payload };
                }
                let json;
                try {
                    json = JSON.parse(text);
                } catch (_e) {
                    throw await this.buildApiError({}, undefined, {
                        ...status_ctx,
                        bodySnippet: String(text).slice(0, 4000),
                    });
                }
                if (!response.ok) {
                    if (json.invalid) {
                        const messages = [];
                        for (let [key, error] of Object.entries(json.invalid)) {
                            key = key.replaceAll(/\.\d+/gm, '.*');
                            messages.push(i18n.t(`${this.resources}.invalid.${key}.${error}`));
                        }
                        throw new Error(_.uniq(messages).join(', '));
                    }
                    throw await this.buildApiError(json, undefined, status_ctx);
                }
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }

                if (json.invalid) {
                    const messages = [];
                    for (let [key, error] of Object.entries(json.invalid)) {
                        key = key.replaceAll(/\.\d+/gm, '.*');
                        messages.push(i18n.t(`${this.resources}.invalid.${key}.${error}`));
                    }
                    throw new Error(_.uniq(messages).join(', '));
                }

                throw await this.buildApiError(json);
            });
    }

    duplicate(payload, options) {
        this.assertNotReadonlyEdition();
        payload = new this.model(payload);
        if ('prepareForDuplicate' in payload)
            payload = payload.prepareForDuplicate(options);
        if ('prepareForUpdate' in payload)
            payload = payload.prepareForUpdate();

        return this.POST(`/${this.entryPoint}${this.CREATE_PATH}`, {
            lang: i18n.language,
            [this.resource]: payload,
        })
            .then(response => this.parseResponseJson(response))
            .then(async (json) => {
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }

                if (json.invalid) {
                    const messages = [];
                    for (let [key, error] of Object.entries(json.invalid)) {
                        key = key.replaceAll(/\.\d+/gm, '.*');
                        messages.push(i18n.t(`${this.resources}.invalid.${key}.${error}`));
                    }
                    throw new Error(_.uniq(messages).join(', '));
                }

                throw await this.buildApiError(json);
            });
    }

    delete(id) {
        this.assertNotReadonlyEdition();
        return this.POST(`/${this.entryPoint}${this.DELETE_PATH}/${id}`, {})
            .then(response => this.parseResponseJson(response))
            .then(async (json) => {
                if (json.flash && json.flash.error)
                    throw await this.buildApiError(json);

                return json;
            });
    }

    getLinkedResources(resources, params) {
        params = params || {};

        let url = `/${this.entryPoint}/linked_resources`;
        if (resources?.length)
            url += `/resources/${resources.join(',')}`;
        if (params.id)
            url += `/id/${params.id}`;
        if (params.tkt_instance_id)
            url += `/tkt_instance/${params.tkt_instance_id}`;

        return this.GET(url)
            .then(response => this.parseResponseJson(response))
            .then(async (json) => {
                if (json.linked_resources)
                    return json;
                throw await this.buildApiError(json);
            });
    }
}

export default RestClient;
